// Save-and-resume for respondents: the in-progress FormState autosaved to the
// browser's own localStorage, restored behind an explicit "Resume?" choice.
//
// What is saved is deliberately partial. `order` and `widgets` are the
// answers; `touched` is presentation state and starts over; `files` holds
// live File objects that cannot survive serialization, so picked-but-unsent
// files are re-attached by the person (uploaded URLs merged at submit are
// never in FormState at all). A fingerprint of the published definition rides
// along: a form republished with different fields silently discards the
// draft rather than pouring old answers into new shapes.
//
// Every storage access is wrapped — private windows and storage-blocked
// browsers make localStorage itself throw, and a form that cannot autosave
// must still be a form.
import type { FormState, PublishedDef, WidgetInstance } from './model';

const VERSION = 1;
/** Drafts older than this are stale enough to be noise, not help. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface StoredDraft {
  v: number;
  savedAt: number;
  fingerprint: string;
  page: number;
  order: FormState['order'];
  widgets: Record<string, WidgetInstance[]>;
}

/**
 * The half of a draft that is worth sending anywhere — no `v` or `savedAt`,
 * which belong to whoever stored it. This is the body of the server-side
 * autosave (038); the server keeps the same shape so one restore path serves
 * a draft from localStorage and a draft from another device alike.
 */
export type DraftPayload = Pick<StoredDraft, 'fingerprint' | 'page' | 'order' | 'widgets'>;

/**
 * A container's answers with every signature taken out.
 *
 * A signature is the one answer a draft must NOT carry, and it took using the
 * feature to see why. The pad's answer is a base64 PNG, so a draft holding one
 * would: leave somebody's signature sitting in localStorage (and in the
 * server-side draft) for a week after they thought better of submitting; eat
 * most of the partial-save size cap in one field; and — the visible bug —
 * restore a VALUE whose pixels cannot be restored with it, so Resume showed an
 * empty pad that reported itself as signed, and would have submitted a
 * signature the person could not see.
 *
 * Signing again is a moment's work, and it is the moment the audit trail is
 * about. So drafts carry everything else and ask for that one thing fresh.
 */
function withoutSignatures(
  keys: Set<string>, values: Record<string, unknown>
): Record<string, unknown> {
  if (!keys.size) return values;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values)) if (!keys.has(k)) out[k] = v;
  return out;
}

/** The same, through a widget tree — a signature can repeat with a container. */
function instancesWithoutSignatures(
  keys: Set<string>, widgets: Record<string, WidgetInstance[]>
): Record<string, WidgetInstance[]> {
  if (!keys.size) return widgets;
  const walk = (list: WidgetInstance[]): WidgetInstance[] => list.map((inst) => ({
    ...inst,
    values: withoutSignatures(keys, inst.values),
    children: Object.fromEntries(
      Object.entries(inst.children || {}).map(([scope, kids]) => [scope, walk(kids)])
    ),
  }));
  return Object.fromEntries(Object.entries(widgets).map(([scope, list]) => [scope, walk(list)]));
}

export const draftPayload = (def: PublishedDef, state: FormState, page: number): DraftPayload => {
  const signatureKeys = new Set(
    def.fields.filter((f) => f.input_type === 'signature').map((f) => f.key)
  );
  return {
    fingerprint: defFingerprint(def),
    page,
    order: withoutSignatures(signatureKeys, state.order),
    widgets: instancesWithoutSignatures(signatureKeys, state.widgets),
  };
};

export const draftKey = (formPath: string) => `fm-draft:${formPath}`;

/**
 * A cheap identity for the published definition: the sorted field keys plus
 * each widget's root scope. Layout-only edits (copy, styling, reordering)
 * keep the draft; anything that changes what the answers MEAN discards it.
 */
export function defFingerprint(def: PublishedDef): string {
  const fields = def.fields.map((f) => `${f.key}:${f.input_type}`).sort();
  const scopes = (def.widgets || []).map((w) => w.tree?.scope || '').sort();
  return `${VERSION}|${fields.join(',')}|${scopes.join(',')}`;
}

/** Does this state hold anything a person typed — anything worth saving? */
export function hasAnswers(state: FormState): boolean {
  return Object.keys(state.touched).length > 0;
}

export function saveDraft(key: string, def: PublishedDef, state: FormState, page: number): void {
  const draft: StoredDraft = { v: VERSION, savedAt: Date.now(), ...draftPayload(def, state, page) };
  try { window.localStorage.setItem(key, JSON.stringify(draft)); } catch { /* storage unavailable */ }
}

export function clearDraft(key: string): void {
  try { window.localStorage.removeItem(key); } catch { /* storage unavailable */ }
}

/**
 * The saved draft as a restorable FormState — or null when there is none,
 * it is corrupt, too old, or the form has changed underneath it (those are
 * removed on the way out).
 */
export interface ResumableDraft { state: FormState; page: number; savedAt: number }

/**
 * A stored payload as a restorable state, or null.
 *
 * The fingerprint check is the important one and applies to BOTH sources: a
 * form republished into a different shape must discard the draft rather than
 * pour old answers into new fields. `touched` and `files` start empty by
 * design — see this file's header.
 */
export function restoreDraft(
  payload: unknown, def: PublishedDef, savedAt: number, maxAgeMs = MAX_AGE_MS
): ResumableDraft | null {
  const d = payload as DraftPayload | null;
  const usable = !!d
    && Number.isFinite(savedAt) && Date.now() - savedAt <= maxAgeMs
    && d.fingerprint === defFingerprint(def)
    && !!d.order && typeof d.order === 'object'
    && !!d.widgets && typeof d.widgets === 'object';
  if (!usable) return null;
  return {
    // `files` starts empty: live File objects cannot be serialized, so picked
    // files are re-attached by the person. Signatures are absent from the
    // payload itself rather than dropped here — see withoutSignatures.
    state: { order: d.order, widgets: d.widgets, touched: {}, files: {}, signatures: {} },
    page: Number.isInteger(d.page) && d.page >= 0 ? d.page : 0,
    savedAt,
  };
}

export function loadDraft(
  key: string, def: PublishedDef, maxAgeMs = MAX_AGE_MS
): ResumableDraft | null {
  let raw: string | null = null;
  try { raw = window.localStorage.getItem(key); } catch { return null; }
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as StoredDraft;
    const restored = d && d.v === VERSION
      ? restoreDraft(d, def, Number(d.savedAt), maxAgeMs)
      : null;
    if (!restored) { clearDraft(key); return null; }
    return restored;
  } catch {
    clearDraft(key);
    return null;
  }
}
